import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContactForm } from '@metlas/contact';
import './index.css';

/* ── Data ─────────────────────────────────────────────────────── */
const STEPS = [
  {
    n: '01',
    title: 'Ingests & Parses Raw Target Data',
    body: 'The AI reads all source payroll files simultaneously, automatically detecting the structure — employees, earnings codes, check dates, departments, and amounts — regardless of formatting inconsistencies across locations or time periods.',
    bullets: undefined,
  },
  {
    n: '02',
    title: 'Performs Integrity Validation (Automated Tie-Out)',
    body: 'Before any transformation, the AI runs the same checks a senior analyst would:',
    bullets: [
      'Horizontal tie-out — verifies that the sum of all earnings components (Regular + Overtime + Holiday + Commission + …) equals Gross Earnings for every employee, every pay period',
      'Vertical tie-out — confirms that database totals match the control totals at the bottom of each source tab',
      'Discrepancies are flagged immediately — data quality issues surface before the team wastes hours building on unreliable data',
    ],
  },
  {
    n: '03',
    title: 'Cleans, Standardizes & Consolidates',
    body: 'All files are merged into a single, unified dataset with a consistent schema (Location, Employee, Date, Department, Position, Earnings Code, Rate, Hours, Amount). Inconsistencies in naming conventions, date formats, and earnings code classifications are resolved automatically.',
    bullets: undefined,
  },
  {
    n: '04',
    title: 'Populates the DD Template',
    body: "The clean dataset is mapped directly into the firm's deliverable template — earnings broken out by 7 categories across 37 monthly columns, with fiscal year sub-totals and annual comparisons. All amounts are converted to $000s. Cross-footing is built in.",
    bullets: undefined,
  },
  {
    n: '05',
    title: 'Flags Quality of Earnings Risks',
    body: 'For each employee, the AI analyzes compensation trends and flags:',
    bullets: [
      'Unusual monthly spikes — potential one-time bonuses, retroactive adjustments, or earnings manipulation',
      'Overtime surges — may indicate understaffing or operational inefficiency in the target',
      'Classification anomalies — earnings that may be miscategorized, affecting normalized EBITDA',
      'Run-rate vs. non-recurring — helps the deal team distinguish sustainable compensation costs from one-time items',
    ],
  },
  {
    n: '06',
    title: 'Self-Corrects & Re-Verifies',
    body: 'If any formula errors, broken references, or total mismatches are detected, the AI automatically corrects them and re-runs the full validation loop — delivering a clean, verified output without manual intervention.',
    bullets: undefined,
  },
];

const IMPACT = [
  { metric: 'Time to complete', before: '2–3 days per analyst', after: 'Minutes' },
  { metric: 'Tie-out checks', before: 'Manual, error-prone', after: 'Automated, 100% coverage' },
  {
    metric: 'Anomaly detection',
    before: 'Depends on analyst experience',
    after: 'Systematic — every employee, every month',
  },
  {
    metric: 'Risk of errors in deliverable',
    before: 'High (fatigue, copy-paste)',
    after: 'Near-zero (self-correcting)',
  },
  {
    metric: 'Analyst time freed up',
    before: '—',
    after: 'Redirected to judgment-intensive DD tasks',
  },
];

const ADVANTAGES = [
  {
    label: 'Deal-speed automation',
    desc: 'Raw target payroll data transformed into a DD-ready deliverable in minutes, not days.',
  },
  {
    label: 'Automated tie-outs',
    desc: 'Horizontal and vertical reconciliation performed on 100% of transactions, not just samples.',
  },
  {
    label: 'Quality of earnings insight',
    desc: 'AI flags compensation anomalies, run-rate vs. non-recurring items, and classification risks.',
  },
  {
    label: 'Self-auditing & self-correcting',
    desc: 'Errors caught, fixed, and re-verified automatically before delivery.',
  },
  {
    label: 'Scales with deal complexity',
    desc: 'Handles multi-location, multi-year, multi-format source data seamlessly.',
  },
  {
    label: 'Frees your best people',
    desc: 'Analysts spend time on judgment and client interaction, not data wrangling.',
  },
];

/* ── Component ────────────────────────────────────────────────── */
const CaseStudyAccounting: React.FC = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(true);
  }, []);

  return (
    <div className="cs-page">
      <div className="cs-grain" aria-hidden />
      <div className="cs-bg">
        <div className="cs-mesh" />
        <div className="cs-nebula cs-nebula-1" />
        <div className="cs-nebula cs-nebula-2" />
        <div className="cs-nebula cs-nebula-3" />
      </div>

      <div className={`cs-content ${visible ? 'visible' : ''}`}>
        {/* Nav */}
        <nav className="cs-nav">
          <Link to="/" className="cs-back">
            ← Back to Metlas
          </Link>
        </nav>

        {/* Hero */}
        <section className="cs-hero">
          <p className="cs-eyebrow">
            Case Study · Top 25 U.S. Accounting & Advisory Firm · Financial Due Diligence
          </p>
          <h1 className="cs-headline">AI-Powered Financial Due Diligence</h1>
          <p className="cs-hero-sub">From Raw Payroll Data to Deal-Ready Analysis in Minutes</p>

          <div className="cs-metrics">
            <div className="cs-metric">
              <div className="cs-transform-row">
                <span className="cs-val cs-val--before">2–3 Days</span>
                <span className="cs-arrow-text">→</span>
                <span className="cs-val cs-val--after">Minutes</span>
              </div>
              <span className="cs-metric-label">to complete</span>
            </div>
            <div className="cs-metric-divider" aria-hidden />
            <div className="cs-metric">
              <span className="cs-val cs-val--after">100%</span>
              <span className="cs-metric-label">automated tie-out coverage</span>
            </div>
            <div className="cs-metric-divider" aria-hidden />
            <div className="cs-metric">
              <span className="cs-val cs-val--after">Zero</span>
              <span className="cs-metric-label">manual interventions</span>
            </div>
          </div>
        </section>

        <div className="cs-rule" aria-hidden />

        {/* Context */}
        <section className="cs-section">
          <h2 className="cs-section-label">The Context</h2>
          <p className="cs-body">
            In every acquisition, the buy-side advisory team must verify the target company's
            payroll and compensation data as part of <strong>Quality of Earnings (QoE)</strong> and
            financial due diligence. This means reconciling earnings records across multiple
            locations, validating data integrity, identifying unusual compensation trends, and
            delivering a clean, structured analysis — all under{' '}
            <strong>tight deal timelines</strong>.
          </p>
        </section>

        {/* Problem */}
        <section className="cs-section">
          <h2 className="cs-section-label">The Problem</h2>
          <div className="cs-pills">
            <div className="cs-pill">
              <span className="cs-pill-num">6</span>
              <span className="cs-pill-text">
                Raw payroll
                <br />
                earnings files
              </span>
            </div>
            <div className="cs-pill">
              <span className="cs-pill-num">3</span>
              <span className="cs-pill-text">Locations</span>
            </div>
            <div className="cs-pill">
              <span className="cs-pill-num">3 yrs</span>
              <span className="cs-pill-text">
                of history
                <br />
                <em>Dec 2022 – Dec 2025</em>
              </span>
            </div>
          </div>
          <p className="cs-body">
            A leading accounting firm performing financial DD on a target company received these
            files — each containing multiple tabs with thousands of line-item payroll transactions:
            employee names, check dates, earnings codes, rates, hours, and amounts.
          </p>
          <p className="cs-body cs-body--label">The DD team needed to:</p>
          <ul className="cs-list">
            <li>Consolidate all files into a single, auditable dataset</li>
            <li>
              Validate that every earnings breakdown ties to gross pay (no missing or phantom
              compensation)
            </li>
            <li>
              Map the data into the firm's standardized DD template — earnings by category across{' '}
              <strong>37 months</strong>, presented in <strong>$000s</strong>
            </li>
            <li>
              Identify compensation anomalies that could signal{' '}
              <strong>quality of earnings risks</strong> — unexplained spikes, off-cycle payments,
              or classification inconsistencies
            </li>
            <li>Deliver the analysis within the deal's compressed timeline</li>
          </ul>
          <div className="cs-time-callout">
            <span className="cs-time-label">Traditionally</span>
            <span className="cs-time-value">2–3 days</span>
            <span className="cs-time-desc">
              of manual data wrangling, cross-checking, and formatting — time the deal team can't
              afford.
            </span>
          </div>
        </section>

        <div className="cs-rule" aria-hidden />

        {/* Steps */}
        <section className="cs-section">
          <h2 className="cs-section-label">What Our AI Does — Automatically</h2>
          <div className="cs-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="cs-step">
                <div className="cs-step-left">
                  <span className="cs-step-num">{s.n}</span>
                  {i < STEPS.length - 1 && <div className="cs-step-line" />}
                </div>
                <div className="cs-step-body">
                  <h3 className="cs-step-title">{s.title}</h3>
                  <p className="cs-body">{s.body}</p>
                  {s.bullets && (
                    <ul className="cs-list cs-list--compact">
                      {s.bullets.map((b, j) => (
                        <li key={j}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="cs-rule" aria-hidden />

        {/* Impact table */}
        <section className="cs-section">
          <h2 className="cs-section-label">The Impact for Deal Teams</h2>
          <div className="cs-table-wrap">
            <table className="cs-table">
              <thead>
                <tr>
                  <th className="cs-th cs-th--metric"></th>
                  <th className="cs-th cs-th--before">Before (Manual)</th>
                  <th className="cs-th cs-th--after">After (AI-Assisted)</th>
                </tr>
              </thead>
              <tbody>
                {IMPACT.map((row, i) => (
                  <tr key={i} className="cs-tr">
                    <td className="cs-td cs-td--metric">{row.metric}</td>
                    <td className="cs-td cs-td--before">{row.before}</td>
                    <td className="cs-td cs-td--after">{row.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="cs-rule" aria-hidden />

        {/* Key advantages */}
        <section className="cs-section">
          <h2 className="cs-section-label">Key Advantages</h2>
          <div className="cs-advantages">
            {ADVANTAGES.map((a, i) => (
              <div key={i} className="cs-advantage">
                <span className="cs-advantage-label">{a.label}</span>
                <span className="cs-advantage-desc">{a.desc}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <aside className="cs-contact-float">
        <ContactForm variant="float" />
      </aside>
    </div>
  );
};

export default CaseStudyAccounting;
